import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { SunIcon, HeartIcon, HandIcon, SwordIcon, EyeIcon, LeafIcon, StarIcon, Flower2Icon } from 'lucide-react';
const GardenProgressTracker: React.FC = () => {
  const {
    userData
  } = useUser();
  const [activeGarden, setActiveGarden] = useState<string | null>(userData.currentGarden || null);
  // Garden data with visual representations
  const gardens = [{
    value: 'Wisdom',
    name: 'Luminous Labyrinth',
    icon: SunIcon,
    color: 'blue',
    description: 'A garden of knowledge and insight where clarity blooms.',
    progress: userData.gardensRestored >= 0 ? 100 : 0,
    unlocked: true
  }, {
    value: 'Love',
    name: 'Blooming Sanctuary',
    icon: HeartIcon,
    color: 'pink',
    description: 'A garden of compassion where kindness and care flourish.',
    progress: userData.gardensRestored >= 1 ? 100 : 0,
    unlocked: userData.gardensRestored >= 1
  }, {
    value: 'Respect',
    name: 'Harmonious Grove',
    icon: HandIcon,
    color: 'purple',
    description: 'A garden of honor where boundaries and dignity thrive.',
    progress: userData.gardensRestored >= 2 ? 100 : 0,
    unlocked: userData.gardensRestored >= 2
  }, {
    value: 'Bravery',
    name: 'Steadfast Summit',
    icon: SwordIcon,
    color: 'orange',
    description: 'A garden of courage where strength and resilience grow.',
    progress: userData.gardensRestored >= 3 ? 100 : 0,
    unlocked: userData.gardensRestored >= 3
  }, {
    value: 'Honesty',
    name: 'Transparent Pools',
    icon: EyeIcon,
    color: 'yellow',
    description: 'A garden of truth where authenticity reflects clearly.',
    progress: userData.gardensRestored >= 4 ? 100 : 0,
    unlocked: userData.gardensRestored >= 4
  }, {
    value: 'Humility',
    name: 'Whispering Meadow',
    icon: LeafIcon,
    color: 'indigo',
    description: 'A garden of openness where learning and growth flourish.',
    progress: userData.gardensRestored >= 5 ? 100 : 0,
    unlocked: userData.gardensRestored >= 5
  }, {
    value: 'Truth',
    name: 'Eternal Compass',
    icon: StarIcon,
    color: 'green',
    description: 'A garden of wisdom where inner guidance blossoms.',
    progress: userData.gardensRestored >= 6 ? 100 : 0,
    unlocked: userData.gardensRestored >= 6
  }];
  const currentGardenIndex = gardens.findIndex(g => g.value === userData.currentGarden);
  const currentGarden = gardens[currentGardenIndex] || gardens[0];
  return <div className="bg-white rounded-xl shadow-md p-6 h-full relative overflow-hidden" data-id="element-143">
      {/* Decorative elements */}
      <div className={`absolute -top-6 -right-6 text-${currentGarden.color}-200 opacity-20`} data-id="element-144">
        <Flower2Icon size={80} data-id="element-145" />
      </div>
      <div className={`absolute bottom-4 left-4 text-${currentGarden.color}-200 opacity-20`} data-id="element-146">
        <LeafIcon size={40} data-id="element-147" />
      </div>
      <h2 className="text-xl font-semibold mb-4 flex items-center relative z-10" data-id="element-148">
        <Flower2Icon className="mr-2 text-green-600" size={20} data-id="element-149" />
        Sacred Garden Journey
      </h2>
      {/* Current garden status */}
      <div className={`bg-${currentGarden.color}-50 border border-${currentGarden.color}-200 rounded-lg p-4 mb-4 relative overflow-hidden`} data-id="element-150">
        <div className={`absolute bottom-0 right-0 text-${currentGarden.color}-200 opacity-30`} data-id="element-151">
          <currentGarden.icon size={60} data-id="element-152" />
        </div>
        <div className="flex items-center mb-2 relative z-10" data-id="element-153">
          <div className={`bg-${currentGarden.color}-100 p-2 rounded-full mr-3`} data-id="element-154">
            <currentGarden.icon className={`text-${currentGarden.color}-600`} size={20} data-id="element-155" />
          </div>
          <div data-id="element-156">
            <h3 className={`text-lg font-medium text-${currentGarden.color}-800`} data-id="element-157">
              {currentGarden.name}
            </h3>
            <p className="text-sm text-gray-600" data-id="element-158">
              Currently cultivating:{' '}
              <span className="font-medium" data-id="element-159">{currentGarden.value}</span>
            </p>
          </div>
        </div>
        <p className="text-gray-700 mb-3 relative z-10" data-id="element-160">
          {currentGarden.description}
        </p>
        <div className="relative pt-1 relative z-10" data-id="element-161">
          <div className="flex items-center justify-between mb-1" data-id="element-162">
            <div data-id="element-163">
              <span className="text-xs font-medium text-gray-600" data-id="element-164">
                Garden Restoration
              </span>
            </div>
            <div data-id="element-165">
              <span className="text-xs font-medium text-gray-600" data-id="element-166">
                {currentGarden.progress}%
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200" data-id="element-167">
            <div style={{
            width: `${currentGarden.progress}%`
          }} className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-${currentGarden.color}-500`} data-id="element-168"></div>
          </div>
        </div>
      </div>
      {/* Garden map */}
      <div className="mb-4 relative z-10" data-id="element-169">
        <h3 className="text-lg font-medium mb-3" data-id="element-170">Your Garden Map</h3>
        <div className="grid grid-cols-7 gap-2" data-id="element-171">
          {gardens.map((garden, index) => <button key={garden.value} onClick={() => garden.unlocked && setActiveGarden(garden.value)} className={`
                relative flex flex-col items-center p-2 rounded-lg transition-all
                ${garden.unlocked ? `bg-${garden.color}-50 border border-${garden.color}-200 cursor-pointer hover:shadow-md` : 'bg-gray-100 border border-gray-200 opacity-50 cursor-not-allowed'}
                ${activeGarden === garden.value ? 'ring-2 ring-offset-2 ring-green-500' : ''}
              `} data-id="element-172">
              <garden.icon className={`${garden.unlocked ? `text-${garden.color}-600` : 'text-gray-400'}`} size={24} data-id="element-173" />
              <span className="text-xs mt-1 text-center" data-id="element-174">{garden.value}</span>
              {garden.progress === 100 && <div className="absolute -top-1 -right-1 bg-green-500 rounded-full w-4 h-4 flex items-center justify-center" data-id="element-175">
                  <Flower2Icon className="text-white" size={12} data-id="element-176" />
                </div>}
            </button>)}
        </div>
      </div>
      {/* Progress stats */}
      <div className="bg-gray-50 rounded-lg p-4 relative z-10" data-id="element-177">
        <div className="flex justify-between items-center" data-id="element-178">
          <div data-id="element-179">
            <p className="text-sm text-gray-600" data-id="element-180">Gardens Restored</p>
            <p className="text-2xl font-bold text-green-600" data-id="element-181">
              {userData.gardensRestored || 0}/7
            </p>
          </div>
          <div data-id="element-182">
            <p className="text-sm text-gray-600" data-id="element-183">Quests Completed</p>
            <p className="text-2xl font-bold text-purple-600" data-id="element-184">
              {userData.completedQuests?.length || 0}
            </p>
          </div>
          <div data-id="element-185">
            <p className="text-sm text-gray-600" data-id="element-186">Journey Progress</p>
            <p className="text-2xl font-bold text-blue-600" data-id="element-187">
              {Math.round((userData.gardensRestored || 0) / 7 * 100)}%
            </p>
          </div>
        </div>
      </div>
    </div>;
};
export default GardenProgressTracker;